import {
    RenderProposalSlideResponse,
    TemplateAiProposal,
} from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";

/**
 * Client-side memo for PowerPoint slide renders.
 *
 * The rendered PNG is small (measured 73-357 KB); the expensive part is what the SERVER does
 * per toggle — a SharePoint download, a Syncfusion parse and a rasterisation. Toggling a
 * proposal on, off and on again is the dominant review gesture, and every one of those was a
 * full round trip. Memoising by (slide, accepted set) removes the round trip entirely for
 * states already seen, at zero server memory.
 */
export const SLIDE_RENDER_CACHE_MAX_ENTRIES = 30;

/**
 * Byte ceiling for the whole cache, enforced ALONGSIDE the entry count.
 *
 * An entry count alone budgets nothing, because entries differ by an order of magnitude. The
 * stored value is the base64 STRING, not the PNG: a 357 KB photo-heavy slide becomes ~476 KB
 * of base64 and, as a UTF-16 JS string, roughly 950 KB in memory. Thirty of those is ~28 MB
 * retained in an Office task-pane webview — not the "few MB" an entry count suggests, and the
 * typical 73 KB text slide (~195 KB in memory) lands nowhere near it. Budgeting by measured
 * size makes the worst case and the typical case cost the same ceiling: the cache simply
 * holds fewer big slides.
 *
 * 12 MB is comfortably above a full review of ordinary text slides (~60 of them) and well
 * inside what a task pane can hold without pressure.
 */
export const SLIDE_RENDER_CACHE_MAX_BYTES = 12 * 1024 * 1024;

/** Retained cost of one entry: base64 is stored as a UTF-16 string, so two bytes per char. */
const entryBytes = (value: RenderProposalSlideResponse): number =>
    ((value?.imageBase64?.length ?? 0) * 2) + 256; // + a small allowance for key and bounds

/**
 * Stable, order-independent key for one rendered state.
 *
 * Deliberately NOT "the accepted ids": the rendered pixels also depend on the token text, the
 * anchor it replaces, and replaceAll — flip replaceAll and the same ids produce a different
 * image, so an id-only key would serve a stale render. JSON.stringify rather than a delimiter
 * join so a field whose own text contains the delimiter cannot forge another state's key.
 */
export const buildSlideRenderKey = (slideIndex: number, accepted: TemplateAiProposal[]): string => {
    const parts = (accepted ?? [])
        // valueText and occurrenceIndex are part of the identity: one decides where inside the
        // anchor the token lands and the other decides WHICH occurrence gets it, so two states
        // differing only by either render different images.
        .map(p => [p.id, p.anchorText, p.valueText ?? null, p.occurrenceIndex ?? null, p.token, p.replaceAll === true] as const)
        // Sorted so the caller's array order — which is incidental — cannot split the cache.
        .sort((a, b) => (a[0] - b[0]) || String(a[1]).localeCompare(String(b[1])));
    return JSON.stringify([slideIndex, parts]);
};

export interface ISlideRenderCache {
    get(key: string): RenderProposalSlideResponse | undefined;
    set(key: string, value: RenderProposalSlideResponse): void;
    clear(): void;
}

/**
 * Insertion-ordered LRU bounded by BOTH entry count and retained bytes, because a long review
 * across many slides would otherwise hold every state it ever rendered. Whichever ceiling
 * binds first wins: 30 ordinary text slides cost ~6 MB and never reach the byte ceiling, while
 * a deck of photo-heavy slides reaches it at around a dozen entries and evicts from there.
 */
export const createSlideRenderCache = (
    maxEntries: number = SLIDE_RENDER_CACHE_MAX_ENTRIES,
    maxBytes: number = SLIDE_RENDER_CACHE_MAX_BYTES): ISlideRenderCache => {
    const entries = new Map<string, RenderProposalSlideResponse>();
    let bytes = 0;

    const evictOldest = (): boolean => {
        const oldest = entries.keys().next();
        if (oldest.done) return false;
        const evicted = entries.get(oldest.value);
        entries.delete(oldest.value);
        bytes -= entryBytes(evicted);
        return true;
    };

    return {
        get: (key: string) => entries.get(key),
        set: (key: string, value: RenderProposalSlideResponse) => {
            // Delete-then-set so a re-store moves the key to the most-recent position rather
            // than leaving it where it was first inserted and evicting it while still in use.
            // The replaced entry's bytes are released, or the running total drifts upward on
            // every re-store and the cache silently shrinks to nothing.
            if (entries.has(key)) bytes -= entryBytes(entries.get(key));
            entries.delete(key);
            entries.set(key, value);
            bytes += entryBytes(value);

            // `entries.size > 1` keeps the ceiling from evicting the entry just stored: a
            // single slide larger than the whole budget must still be served once, otherwise
            // the cache turns into a guaranteed miss for exactly the decks it helps most.
            while ((entries.size > maxEntries || bytes > maxBytes) && entries.size > 1) {
                if (!evictOldest()) break;
            }
        },
        clear: () => {
            entries.clear();
            bytes = 0;
        },
    };
};
